import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** Draggable class-chip used inside each existing level row. */
const SortableClassChip = ({ id, label, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // Kill iOS Safari's press-and-hold text selection / Copy callout
    touchAction: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    userSelect: 'none',
  };
  return (
    <span
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium cursor-grab active:cursor-grabbing select-none touch-none ${isDragging ? 'ring-2 ring-indigo-400 shadow' : ''}`}
      data-testid={`level-class-chip-${id}`}
      aria-roledescription="Draggable class chip"
      aria-label={`${label}. Press and drag to reorder.`}
    >
      <GripVertical className="h-3 w-3 text-indigo-500" />
      {label}
      {onRemove && (
        <button
          type="button"
          // stop drag from capturing the click / touch on the remove button
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={onRemove}
          className="hover:text-indigo-900"
          style={{ touchAction: 'auto' }}
          aria-label={`Remove ${label}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

/** DnD-enabled list of classes under a single level. */
const LevelClassesDndList = ({ lvl, onPersist }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Dedicated touch sensor — small delay + tolerance so a tap-to-dismiss ✕
    // button doesn't accidentally start a drag on mobile.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const classes = lvl.classes || [];

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = classes.indexOf(active.id);
    const newIndex = classes.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(classes, oldIndex, newIndex);
    onPersist(lvl.id, reordered);
  };

  const handleRemove = (name) => {
    onPersist(lvl.id, classes.filter(c => c !== name));
  };

  if (classes.length === 0) {
    return <p className="text-xs text-slate-400 italic">No classes</p>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={classes} strategy={horizontalListSortingStrategy}>
        <div className="flex flex-wrap gap-1.5" data-testid={`level-classes-dnd-${lvl.id}`}>
          {classes.map(c => (
            <SortableClassChip
              key={c}
              id={c}
              label={c}
              onRemove={() => handleRemove(c)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

/**
 * Class Levels configuration section.
 *
 * Owns its own form state (levelForm, levelClassInput). The parent owns the
 * persisted `classLevels` list so other sections (e.g. Exam Freeze by Level)
 * can reference it — a `refetchLevels` callback keeps everything in sync.
 */
export const ClassLevelsSection = ({ API, token, classLevels, refetchLevels }) => {
  const [levelForm, setLevelForm] = useState({ name: '', order: 1, classes: [] });
  const [levelClassInput, setLevelClassInput] = useState('');

  const addLevelClassChip = (raw) => {
    const name = (raw || '').trim();
    if (!name) return;
    setLevelForm(f => f.classes.includes(name) ? f : { ...f, classes: [...f.classes, name] });
    setLevelClassInput('');
  };

  const handleAddLevel = async () => {
    if (!levelForm.name.trim()) { toast.error('Level name required'); return; }
    try {
      await axios.post(`${API}/class-levels`, levelForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Level created');
      setLevelForm({ name: '', order: (classLevels.length || 0) + 1, classes: [] });
      setLevelClassInput('');
      refetchLevels?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteLevel = async (id) => {
    try {
      await axios.delete(`${API}/class-levels/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Level removed');
      refetchLevels?.();
    } catch { toast.error('Failed'); }
  };

  const handleSeedLevels = async () => {
    try {
      const res = await axios.post(`${API}/class-levels/seed-defaults`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      refetchLevels?.();
    } catch { toast.error('Failed'); }
  };

  // Persist a new class order (or removal) on a specific level.
  const persistLevelClasses = async (levelId, newClasses) => {
    try {
      await axios.patch(
        `${API}/class-levels/${levelId}`,
        { classes: newClasses },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetchLevels?.();
    } catch {
      toast.error('Failed to save order');
    }
  };

  return (
    <section data-testid="section-levels" id="section-levels" className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-1 rounded bg-indigo-500" />
        <h2 className="text-2xl font-semibold text-slate-900">Class Levels</h2>
      </div>

      <Card className="dashboard-card p-6" data-testid="class-levels-card">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Group classes by level</h3>
            <p className="text-sm text-slate-500 mt-1">
              Organise classes into levels (e.g., Upper / Lower Secondary) for level-scoped freeze, level-matched relief, and filter controls.
              Drag chips to reorder — the new sequence is used everywhere classes are iterated.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleSeedLevels} data-testid="seed-levels-button">
            Auto-seed from timetable
          </Button>
        </div>

        {/* Create new level */}
        <div className="p-3 border border-slate-200 rounded-md bg-slate-50/50 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Level Name</Label>
              <Input
                value={levelForm.name}
                onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                placeholder="e.g., Upper Secondary"
                className="mt-1 bg-white"
                data-testid="level-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Sort Order</Label>
              <Input
                type="number"
                value={levelForm.order}
                onChange={(e) => setLevelForm({ ...levelForm, order: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-white"
                data-testid="level-order-input"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddLevel} className="bg-indigo-700 hover:bg-indigo-800 w-full" data-testid="add-level-button">
                Add Level
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Classes under this level</Label>
            <div className="mt-1 min-h-[42px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-slate-200 rounded-md bg-white"
                 data-testid="level-classes-input">
              {levelForm.classes.map(c => (
                <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium">
                  {c}
                  <button type="button" onClick={() => setLevelForm(f => ({ ...f, classes: f.classes.filter(x => x !== c) }))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={levelClassInput}
                onChange={(e) => setLevelClassInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addLevelClassChip(levelClassInput);
                  }
                }}
                placeholder={levelForm.classes.length === 0 ? 'Type class names, press Enter' : 'Add more...'}
                className="flex-1 min-w-[160px] text-sm outline-none bg-transparent py-0.5"
              />
            </div>
          </div>
        </div>

        {/* Existing levels */}
        <div className="space-y-2" data-testid="class-levels-list">
          {classLevels.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No levels configured yet. Use &quot;Auto-seed&quot; or create one above.</p>
          ) : classLevels.map(lvl => (
            <div key={lvl.id} className="flex items-start justify-between gap-3 p-3 bg-white border border-indigo-100 rounded-md">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 mb-1.5">
                  {lvl.name} <span className="text-xs text-slate-400 font-normal">· order {lvl.order}</span>
                </p>
                <LevelClassesDndList lvl={lvl} onPersist={persistLevelClasses} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDeleteLevel(lvl.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-level-${lvl.id}`}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};
